<div class="my-5">
  <h1>Secure Choice Lending Terms and Conditions</h1>
  <p><strong>Effective Date: 3/26/2026</strong></p>
  <p>These Terms and Conditions ("Terms") govern your access to and use of Secure Choice Lending ("SCL," "we," "us," or "our") websites, services, platforms, tools, CRM systems, AI systems, marketing platforms, and related applications (collectively, the "Services").</p>
  <p>By accessing or using the Services, you agree to be bound by these Terms.</p>

  <h5>1. Eligibility and Use of Services</h5>
  <p>You must be at least 18 years of age to access or use the Services. By using the Services, you represent and warrant that you meet this requirement.</p>
  <p>You agree to use the Services only for lawful purposes and in compliance with all applicable laws, including but not limited to TCPA, CAN-SPAM, RESPA, and applicable state privacy laws.</p>
  <p>You may not:</p>
  <ul>
    <li>Use the Services for any unlawful, fraudulent, or deceptive purpose</li>
    <li>Violate any federal or state marketing or communication laws</li>
    <li>Attempt to gain unauthorized access to systems, accounts, or data</li>
    <li>Reverse engineer, copy, modify, or exploit the platform</li>
  </ul>

  <h5>2. Accounts and Security</h5>
  <p>Certain features require account access.</p>
  <p>You are responsible for:</p>
  <ul>
    <li>Maintaining confidentiality of login credentials</li>
    <li>All activity under your account</li>
    <li>Ensuring authorized use of your account</li>
  </ul>
  <p>SCL may suspend or terminate accounts at its sole discretion for violations of these Terms.</p>

  <h5>3. Platform, CRM, and Marketing Systems</h5>
  <p>SCL provides proprietary tools including CRM systems, AI tools, marketing automation systems, and communication platforms.</p>
  <p>You agree to:</p>
  <ul>
    <li>Use these systems solely for legitimate business purposes</li>
    <li>Comply with all applicable marketing and communication laws</li>
    <li>Maintain appropriate consent records for communications</li>
  </ul>
  <p>You may not:</p>
  <ul>
    <li>Export, sell, or distribute consumer data</li>
    <li>Use the platform to send unlawful or unsolicited communications</li>
    <li>Use the platform for mass messaging without proper consent</li>
    <li>Scrape, copy, or extract data from the platform</li>
  </ul>

  <h5>4. Referral Partner and Third-Party Use</h5>
  <p>SCL may provide access to tools, CRM systems, and platforms to referral partners, including licensed real estate professionals and brokers.</p>
  <p><strong>Conditions of Access:</strong></p>
  <p>Access to SCL platforms is provided solely for legitimate business purposes, including transaction coordination, client communication, and marketing support. Access is not conditioned on the referral of mortgage business to SCL, and no agreement, expectation, or requirement exists for any partner to refer mortgage transactions to SCL.</p>
  <p><strong>RESPA Acknowledgment:</strong></p>
  <p>By accessing SCL tools and platforms, referral partners expressly acknowledge that:</p>
  <ul>
    <li>Platform access does not constitute, and shall not be construed as, compensation for the referral of mortgage business</li>
    <li>No quid pro quo arrangement exists or is implied between platform access and the referral of any mortgage transaction</li>
    <li>Each partner is independently responsible for compliance with applicable law, including RESPA</li>
    <li>SCL does not guarantee referral partners any business, leads, or compensation in exchange for use of its platforms</li>
  </ul>
  <p><strong>Partner Data Access:</strong></p>
  <p>Referral partners are provided access only to data associated with their own clients and activities within the platform.</p>
  <p>SCL implements access controls to restrict visibility to authorized data only. Users are responsible for ensuring that any data accessed through the platform is used solely for legitimate business purposes and in compliance with applicable laws.</p>
  <p><strong>Prohibited Conduct:</strong></p>
  <p>Users may not:</p>
  <ul>
    <li>Misuse or improperly access consumer data</li>
    <li>Share, sell, or transfer data to unauthorized parties</li>
    <li>Use data outside permitted business purposes</li>
    <li>Represent SCL tools or outputs as independent financial or legal advice</li>
  </ul>

  <h5>5. AI Tools and Automated Systems</h5>
  <p>SCL uses and provides AI-powered tools and automated systems. The following terms apply based on the context of use.</p>
  <p><strong>5A. AI for Communications and Marketing</strong></p>
  <p>AI tools may be used to generate, personalize, schedule, or deliver marketing and customer communications, including emails, SMS messages, and voice communications. These outputs:</p>
  <ul>
    <li>May be generated without human review of each individual message</li>
    <li>May contain inaccuracies or errors</li>
    <li>Are provided for informational and marketing purposes only and do not constitute financial, legal, or lending advice</li>
  </ul>
  <p><strong>5B. AI for Lending Support</strong></p>
  <p>AI tools may assist with certain lending-related functions, including preliminary eligibility screening, document classification, product recommendations, and risk analysis. With respect to these functions:</p>
  <ul>
    <li>AI outputs are reviewed by qualified SCL personnel and do not constitute final lending decisions</li>
    <li>Automated systems do not make final credit determinations independently</li>
    <li>Consumers retain all rights under applicable law, including ECOA and FCRA, and may request human review of decisions and additional information regarding adverse action</li>
  </ul>
  <p><strong>5C. AI Acceptable Use</strong></p>
  <p>Users of SCL AI tools agree not to:</p>
  <ul>
    <li>Input false, misleading, or fraudulent data into AI systems</li>
    <li>Use AI-generated outputs to provide unauthorized financial, legal, or lending advice to third parties</li>
    <li>Use AI tools to generate deceptive, misleading, or non-compliant marketing materials</li>
    <li>Attempt to reverse engineer, extract training data from, or manipulate SCL AI systems</li>
  </ul>
  <p>SCL reserves the right to suspend or terminate access to AI tools for any violation of this section. Users are solely responsible for how they use AI-generated outputs.</p>

  <h5>6. Mortgage and Financial Disclaimers</h5>
  <ul>
    <li>Loan approval is not guaranteed</li>
    <li>Interest rates and loan terms are subject to change</li>
    <li>Estimates, scenarios, and projections are not final loan terms</li>
    <li>Nothing on the platform constitutes a commitment to lend</li>
  </ul>

  <h5>7. Communications and Consent</h5>
  <p>SCL may contact you via email, phone, SMS, or other channels in connection with your use of the Services.</p>
  <p><strong>Marketing Communications:</strong></p>
  <p>Where required by law, SCL will obtain prior express written consent before sending marketing communications via automated systems, including:</p>
  <ul>
    <li>Automatic telephone dialing systems (autodialers)</li>
    <li>Prerecorded or artificially generated voice messages</li>
    <li>Ringless voicemail</li>
    <li>Automated SMS/text messages</li>
  </ul>
  <p>Consent language, opt-out mechanisms, and contact disclosures are governed by SCL's Privacy Policy, Appendix B (Marketing and Communications Consent Language), which is incorporated herein by reference.</p>
  <p>Consent is never required as a condition of obtaining a loan or any other service from SCL.</p>
  <p><strong>Third-Party Leads:</strong></p>
  <p>If SCL receives your contact information from a third-party lead source, SCL will only initiate automated or AI-assisted outreach after verifying that valid prior express written consent exists for contact by Secure Choice Lending specifically, or after obtaining consent directly from you.</p>
  <p><strong>Opt-Out:</strong></p>
  <p>You may opt out of marketing communications at any time by:</p>
  <ul>
    <li>Replying STOP to any SMS message</li>
    <li>Clicking the unsubscribe link in any marketing email</li>
    <li>Contacting us at info&#64;securechoicelending.com or 951-733-8925</li>
  </ul>
  <p>Opting out of marketing communications does not affect transactional or loan-related communications required by law or contract. Your consent to receive marketing communications may be revoked at any time using the opt-out methods described above.</p>

  <h5>8. Intellectual Property</h5>
  <p>All content, systems, software, and materials provided by SCL are the exclusive property of SCL.</p>
  <p>Users are granted a limited, revocable, non-transferable license to use the Services.</p>

  <h5>9. Third-Party Services</h5>
  <p>SCL may integrate with or provide referrals or introductions to third-party service providers in connection with the Services.</p>
  <p>These providers may include, but are not limited to, services related to a borrower's transaction or additional resources that may be relevant to the user.</p>
  <p>Such referrals or introductions are provided for convenience only. SCL does not require the use of any third-party provider, and users are free to select any provider of their choice.</p>
  <p>In some cases, SCL may refer users to service providers that are affiliated with, owned by, or under common control with SCL. Where required by law, appropriate disclosures regarding such relationships will be provided.</p>
  <p>SCL does not guarantee, control, or assume responsibility for the services, actions, or omissions of any third-party provider.</p>

  <h5>10. Disclaimer of Warranties</h5>
  <p>The Services are provided "as is" and "as available" without warranties of any kind.</p>
  <p>SCL disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>

  <h5>11. Limitation of Liability</h5>
  <p>To the fullest extent permitted by law, SCL shall not be liable for:</p>
  <ul>
    <li>Indirect, incidental, or consequential damages</li>
    <li>Loss of profits, revenue, or business opportunities</li>
    <li>Errors, interruptions, or inaccuracies in the Services</li>
  </ul>
  <p>In no event shall SCL's total liability exceed the amount paid (if any) by you for use of the Services.</p>

  <h5>12. Indemnification</h5>
  <p>You agree to indemnify, defend, and hold harmless SCL from any claims, damages, or liabilities, including but not limited to claims arising from marketing, communications, or data use, arising from:</p>
  <ul>
    <li>Your use of the Services</li>
    <li>Your violation of these Terms</li>
    <li>Your violation of any law or regulation</li>
    <li>Your misuse of data or communications</li>
  </ul>

  <h5>13. Dispute Resolution and Arbitration</h5>
  <p><strong>Informal Resolution First:</strong></p>
  <p>Before initiating arbitration, you agree to contact SCL at info&#64;securechoicelending.com and provide written notice of your dispute. SCL will attempt to resolve the dispute informally within 30 days of receiving notice.</p>
  <p><strong>Binding Arbitration:</strong></p>
  <p>If informal resolution fails, any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be resolved by binding individual arbitration administered by the American Arbitration Association (AAA) under its applicable rules. The arbitration shall take place in Riverside County, California, or remotely if agreed by both parties.</p>
  <p><strong>Arbitration Costs:</strong></p>
  <p>For consumer disputes, SCL will pay arbitration filing fees and AAA administrative fees, except where the arbitrator determines the claim is frivolous. Each party bears its own attorney's fees unless applicable law provides otherwise.</p>
  <p><strong>Small Claims Exception:</strong></p>
  <p>Either party may bring an individual claim in small claims court in lieu of arbitration, provided the claim qualifies under that court's jurisdictional limits.</p>
  <p><strong>Class Action Waiver:</strong></p>
  <p>All claims must be brought in an individual capacity. You waive any right to participate in class action litigation or class-wide arbitration.</p>
  <p><strong>Opt-Out Right:</strong></p>
  <p>You may opt out of this arbitration agreement by sending written notice to info&#64;securechoicelending.com within 30 days of first accepting these Terms. Your notice must include your name, address, and a clear statement that you are opting out of arbitration. Opting out does not affect any other provision of these Terms.</p>
  <p><strong>Exceptions:</strong></p>
  <p>This arbitration agreement does not apply to claims for injunctive or equitable relief related to unauthorized use, IP infringement, or data security breaches, or where prohibited by law.</p>
  <p>This arbitration provision shall survive termination of these Terms.</p>

  <h5>14. Termination</h5>
  <p>SCL may suspend or terminate access to the Services at any time, with or without notice, for any violation of these Terms.</p>

  <h5>15. Governing Law</h5>
  <p>These Terms shall be governed by the laws of the State of California.</p>

  <h5>16. Changes to Terms</h5>
  <p>SCL may update these Terms at any time. Continued use of the Services constitutes acceptance of the revised Terms.</p>

  <h5>17. Contact Information</h5>
  <p class="mb-0">Secure Choice Lending</p>
  <p class="mb-0">1650 Spruce Street, Suite 500</p>
  <p class="mb-0">Riverside, CA 92507</p>
  <p class="mb-0">info&#64;securechoicelending.com</p>
  <p>951-733-8925</p>

  <h5>18. General Provisions</h5>
  <p><strong>Severability:</strong></p>
  <p>If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision shall be deemed severable and shall not affect the validity or enforceability of the remaining provisions.</p>
  <p><strong>Force Majeure:</strong></p>
  <p>SCL shall not be liable for any failure or delay in performance resulting from causes beyond its reasonable control, including acts of God, natural disasters, pandemics, war, terrorism, government action, third-party outages, or cyberattacks.</p>
  <p><strong>Entire Agreement:</strong></p>
  <p>These Terms, together with SCL's Privacy Policy and any applicable agreements, constitute the entire agreement between you and SCL.</p>
  <p><strong>No Waiver:</strong></p>
  <p>SCL's failure to enforce any provision shall not constitute a waiver of its right to enforce that provision in the future.</p>
  <p><strong>Relationship of Parties:</strong></p>
  <p>Nothing in these Terms creates a partnership, joint venture, agency, or employment relationship.</p>
</div>